import { PhotoIcon } from "@heroicons/react/24/outline";
import { memo, useState } from "react";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityYardSignPhoto } from "@/activity-feed/query";
import { useBusiness } from "@/core/business/use-business";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { DetailCard } from "./common";

const FailedToLoadImage = () => (
  <div className="flex h-[180px] w-[226px] shrink-0 flex-col items-center justify-center gap-2 rounded-lg border border-stroke-tertiary bg-card">
    <PhotoIcon className="h-6 w-6" />
    <Typography variant="bodyW2" textColor="secondary" className="text-center">
      Could not load photo
    </Typography>
  </div>
);

interface YardSignPhotoDetailCardProps {
  photoId: string;
  inView: boolean;
}

export const YardSignPhotoDetailCard = memo(
  ({ photoId, inView }: YardSignPhotoDetailCardProps) => {
    const { businessId } = useBusiness();
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const { data: photo, isLoading } = useGetActivityYardSignPhoto(
      businessId,
      photoId,
      inView,
    );

    // Show loading only if we don't have data yet
    if (!photo && (!inView || isLoading)) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.yard_sign_photo}>
          <Skeleton className="h-[180px] w-[100px] rounded-lg" />
        </DetailCard>
      );
    }

    if (!photo?.photo_url) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.yard_sign_photo}>
          <FailedToLoadImage />
        </DetailCard>
      );
    }

    return (
      <DetailCard eventType={SPIFF_RULE_EVENTS.yard_sign_photo}>
        <Stack direction="row" gap="md" align="start" fullWidth>
          {imageLoadFailed || !photo?.photo_url ? (
            <FailedToLoadImage />
          ) : (
            <img
              src={photo.photo_url}
              alt="Yard sign"
              className="max-h-[180px] min-h-[180px] w-auto max-w-[180px] shrink-0 rounded-lg border border-stroke-tertiary object-contain"
              onError={() => {
                setImageLoadFailed(true);
              }}
            />
          )}
          {photo.service_titan_job_id && (
            <Stack direction="row" gap="xs">
              <Typography>Associated Job:</Typography>
              <ServiceTitanJobLink jobId={photo.service_titan_job_id} />
            </Stack>
          )}
        </Stack>
      </DetailCard>
    );
  },
);

YardSignPhotoDetailCard.displayName = "YardSignPhotoDetailCard";
